/**
 * uploadBytes, wrapped so the auto-update path can see that bytes are moving,
 * plus the upload timeout race - both in one place because they have to
 * agree about the in-flight counter.
 *
 * Every Storage write in the Hub imports uploadBytes from HERE rather than
 * from firebase/storage. That is the whole point: an in-flight upload is
 * invisible to any DOM inspection, so without a chokepoint the version-
 * freshness auto-reload (see freshness.ts) would happily throw away a photo
 * or a music file part-way through sending it. Several call sites also write
 * their Firestore doc BEFORE the file lands, so an interrupted upload leaves
 * a record pointing at something that never arrived.
 *
 * The timeout race lives here too (offerings.ts re-exports it under its
 * original withPhotoUploadTimeout name) because the review caught the two
 * mechanisms fighting: when the 30s timeout WINS the race, the losing upload
 * promise may never settle at all - that is exactly the stalled-fetch case
 * the timeout exists for - so a plain try/finally around the upload never
 * releases the counter and auto-update is silently disabled for the life of
 * the tab. The ticket system in unsavedWork.ts resolves it: the timeout
 * releases the abandoned chain's ticket at timeout-loss, and the settled
 * flag makes the real promise's eventual finally a no-op instead of a
 * double decrement.
 *
 * The resumable variant below follows the same rule: any upload path that
 * needs uploadBytesResumable (a long video, where a 30s timeout would just
 * lose the race) wraps it here rather than importing it directly, so it
 * still shares the one ticket-accounting mechanism the rest of this file
 * describes.
 */

import {
  uploadBytes as firebaseUploadBytes,
  uploadBytesResumable as firebaseUploadBytesResumable,
  type StorageReference,
  type UploadMetadata,
  type UploadResult,
} from 'firebase/storage';
import { abandonOldestUpload, openUploadTicket, settleUploadTicket } from './unsavedWork';

export async function uploadBytes(
  ref: StorageReference,
  data: Blob | Uint8Array | ArrayBuffer,
  metadata?: UploadMetadata
): Promise<UploadResult> {
  const ticket = openUploadTicket();
  try {
    return await firebaseUploadBytes(ref, data, metadata);
  } finally {
    // finally, not a success path: a rejected upload has to release the
    // counter too. A NEVER-settling upload is the one case finally cannot
    // cover, and that is what withUploadTimeout's abandon path is for.
    settleUploadTicket(ticket);
  }
}

/**
 * uploadBytesResumable, wrapped with the same ticket accounting as
 * uploadBytes above, for uploads that can legitimately run for minutes
 * (a 500MB class video) rather than seconds. Deliberately NO timeout race
 * on this path: PHOTO_UPLOAD_TIMEOUT_MS below exists because a stalled
 * fetch with no progress signal can hang forever with nothing to show for
 * it, and a resumable upload's own state_changed progress IS that signal -
 * a caller watching onProgress already knows "slow" from "stalled" without
 * a synthetic clock, and a large file racing the same 30s clock would just
 * lose every time.
 *
 * onProgress, when passed, fires on every state_changed event with
 * bytesTransferred/totalBytes as a 0..1 fraction (skipped if totalBytes is
 * not yet known, which the SDK reports as 0 before the first chunk lands).
 *
 * signal, when passed, cancels the underlying UploadTask on abort.
 * task.cancel() is what feeds the task's own error branch (Firebase resolves
 * a canceled task's error with code 'storage/canceled'), so the ticket still
 * settles and the promise still rejects through the exact same path a real
 * upload failure would - no separate reject-on-abort needed. Wired even
 * though no caller in this PR passes one yet: nothing about the two current
 * callers (offerings.ts, tasks.ts) is complicated by an unused optional
 * parameter, and it means a future "Cancel upload" button costs nothing new
 * here.
 */
export async function uploadBytesResumable(
  ref: StorageReference,
  data: Blob | Uint8Array | ArrayBuffer,
  metadata?: UploadMetadata,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal
): Promise<UploadResult> {
  const ticket = openUploadTicket();
  try {
    const task = firebaseUploadBytesResumable(ref, data, metadata);
    if (signal) {
      if (signal.aborted) {
        task.cancel();
      } else {
        signal.addEventListener('abort', () => task.cancel(), { once: true });
      }
    }
    return await new Promise<UploadResult>((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => {
          if (onProgress && snapshot.totalBytes > 0) {
            onProgress(snapshot.bytesTransferred / snapshot.totalBytes);
          }
        },
        reject,
        () => resolve({ ref: task.snapshot.ref, metadata: task.snapshot.metadata })
      );
    });
  } finally {
    // Same reasoning as uploadBytes' finally above: this has to release the
    // ticket on every outcome (success, a real error, a cancel, or even a
    // synchronous throw from firebaseUploadBytesResumable itself), not just
    // the happy path.
    settleUploadTicket(ticket);
  }
}

// uploadBytes/getDownloadURL have no built-in timeout, and on a bad signal the
// underlying fetch can simply never settle - Bobbie hit exactly this,
// "Saving..." for over a minute with no error and no way out. This bounds the
// wait so a stalled photo upload always fails predictably instead of hanging
// forever. The MESSAGE is the caller's job, because the truth differs per
// call site: where the upload runs before any doc write, a timeout means
// nothing changed; where the doc is already committed before the photo
// starts, telling someone to re-save would mint a duplicate.
export const PHOTO_UPLOAD_TIMEOUT_MS = 30_000;

export function withUploadTimeout<T>(pending: Promise<T>, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      // The chain being raced is abandoned as of this moment: the caller is
      // about to show its failure message. Release its counter claim so a
      // stalled never-settling upload cannot hold auto-update hostage.
      abandonOldestUpload();
      reject(new Error(timeoutMessage));
    }, PHOTO_UPLOAD_TIMEOUT_MS);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}
